import React from 'react';

import { Button, Heading, Modal, Toggle } from '@auspost/postmaster-react';

import { useNotificationSubscriptionAuthed } from 'App/DetailsPage/hooks/subscription.hooks';
import CheckIcon from 'assets/success-icon.svg';
import { updateNotificationPreference } from 'store/shipment/details/shipmentDetails.slice';
import { saveNickname } from 'store/shipment/thunks/saveNickname.thunk';
import { useAppDispatch, useAppSelector } from 'store/store';
import { reportNewRelicError } from 'utils/window.utils';

import { selectIsAuthed } from '../../../../store/customer';
import { clearNotification } from '../../../../store/notification/notification.slice';
import { useArticles, useShipment } from '../../hooks/shipment.hooks';
import NicknameInput from '../NicknameInput/NicknameInput';
import {
  ARTICLE_MODAL_BUTTON_TEST_ID,
  ARTICLE_MODAL_NICKNAME_TEST_ID,
  ARTICLE_MODAL_NOTIFICATION_TOGGLE_TEST_ID,
} from './AddArticleModal.test-ids';

import styles from './AddArticleModal.module.scss';

interface AddArticleModalProp {
  handleClose: () => void;
  isOpen: boolean;
}

const AddArticleModal: React.FC<AddArticleModalProp> = ({ handleClose, isOpen }) => {
  const dispatch = useAppDispatch();
  const isAuthed = useAppSelector(selectIsAuthed);
  const articles = useArticles();
  const shipment = useShipment();
  const loading = useAppSelector((state) => state.shipmentDetails.saveNickname?.loading);
  const [inputValue, setInputValue] = React.useState('');
  const [notificationOn, setIsNotificationOn] = React.useState(true);
  const [isInitialClick, setIsInitialClick] = React.useState(false);
  const { disableNotificationSubscription, enableNotificationSubscription } = useNotificationSubscriptionAuthed();

  if (!isAuthed || !shipment) return null;
  const { consignmentId } = shipment;

  const trackingId = articles.length === 1 ? articles[0].articleId : consignmentId;

  if (!trackingId || !articles?.[0].articleId) return null;
  const updateNickname = async (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    await dispatch(saveNickname({ trackingId: consignmentId, nickname: inputValue }));
    handleClose();
  };

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLElement, MouseEvent>) => {
    updateNickname(e);
    const subscriptionAction = (!notificationOn && disableNotificationSubscription) || enableNotificationSubscription;

    // Prevent notification pill when toggle has not been manually triggered
    subscriptionAction()
      .then((res) => {
        dispatch(updateNotificationPreference(res));
        if (!isInitialClick) {
          dispatch(clearNotification());
        }
      })
      .catch((err) => reportNewRelicError(err));
  };

  return (
    <Modal
      isModalShowing={isOpen}
      onHide={handleClose}
      modalClassName={styles.addArticleModal}
      dialogClassName={styles.addArticleDialog}
      ariaDialogLabel='add article modal'
      autoFocus={false}
      dialogId='add article modal'
    >
      <Modal.Header closeButton={false} className={styles.header}>
        <CheckIcon />
        <Heading level='h3'>Parcel added</Heading>
      </Modal.Header>

      <Modal.Content className={styles.content} onKeyDown={(e) => e.key === 'Escape' && handleClose()}>
        <div>
          <span className={styles.trackingNumber}>{trackingId}</span>
          <br /> has been added to your tracklist
        </div>
        <form
          className={styles.nicknameForm}
          data-testid={ARTICLE_MODAL_NICKNAME_TEST_ID}
          id='add-article-modal-form'
          onSubmit={handleOnSubmit}
        >
          <label className={styles.nameItem} htmlFor='add-article-modal-nickname-input'>
            Name this parcel <span>(optional)</span>
          </label>
          <NicknameInput
            nicknameId='add-article-modal-nickname-input'
            inputValue={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        </form>

        <div className={styles.divider} />

        <div className={styles.notificationToggle}>
          <label id='add-modal-notification-label'>Notifications for this delivery</label>
          <Toggle
            id='addModalNotificationToggle'
            aria-labelledby='add-modal-notification-label'
            data-testid={ARTICLE_MODAL_NOTIFICATION_TOGGLE_TEST_ID}
            isToggled={notificationOn}
            onChange={() => setIsNotificationOn(!notificationOn)}
            onClick={() => setIsInitialClick(true)}
          />
        </div>
      </Modal.Content>

      <Button
        variant='primary'
        size='medium'
        form='add-article-modal-form'
        data-testid={ARTICLE_MODAL_BUTTON_TEST_ID}
        className={styles.continueButton}
        isLoading={loading}
      >
        Continue
      </Button>
    </Modal>
  );
};

export default AddArticleModal;
